import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import {
  Form,
  message,
  Button,
  Input,
  InputNumber,
  Spin,
  Select,
  DatePicker,
  Modal,
  Table,
  Skeleton,
} from "antd";
import { PlusOutlined, UserOutlined } from "@ant-design/icons";
import { NotificationOutlined } from "@ant-design/icons";
import {
  createCampaign,
  updateCampaign,
  getCampaignById,
  getCampaignClientSegmentIds,
  getClientSegmentsFilteredLite,
  getPromotions,
  getRewards,
  getRewardClients,
  getRewardById,
} from "../../../api/Services";

import styles from "./NewCampaign.module.scss";
import {
  hasPermission,
  privilegesEnum,
} from "../../../helpers/PermissionHelper";
import { COMMERCIAL_INTELLIGENCE_PATHS } from "../../../helpers/MenuPathHelper";
import CustomBreadcrumb from "../../../components/breadcrumb/Breadcrumb";
import moment from "moment";

const { RangePicker } = DatePicker;

const NewCampaign = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const getBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/campaigns";
    }
    return "/promotions/campaigns";
  };

  const [loading, setLoading] = useState(false);
  // Create: paint form immediately. Edit: skeleton until campaign loads.
  const [loadingData, setLoadingData] = useState(!!id);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [clientSegments, setClientSegments] = useState<any[]>([]);
  const [promotions, setPromotions] = useState<any[]>([]);
  const [rewards, setRewards] = useState<any[]>([]);
  const [campaign, setCampaign] = useState<any>(null);
  const [fromRewardRef, setFromRewardRef] = useState<{
    rewardId: number;
    rewardName?: string;
    clientCount?: number;
  } | null>(null);
  const [clientsModalOpen, setClientsModalOpen] = useState(false);
  const [clientsModalLoading, setClientsModalLoading] = useState(false);
  const [clientsModalData, setClientsModalData] = useState<{
    content: any[];
    totalElements: number;
  }>({ content: [], totalElements: 0 });
  const [clientsModalPage, setClientsModalPage] = useState(0);
  const clientsModalPageSize = 20;

  const [form] = Form.useForm();

  const isEdit = !!id;

  useEffect(() => {
    const loadData = async () => {
      if (!hasPermission(privilegesEnum.WRITE_CAMPAIGNS)) {
        setLoadingData(false);
        return;
      }

      // Prefetch select options in background — do not block create UI.
      const prefetch = Promise.all([
        handleSearchClientSegments(""),
        handleSearchPromotions(""),
        handleSearchRewards(""),
      ]).catch((error) => {
        console.error("Error loading campaign form options:", error);
      });

      if (isEdit) {
        try {
          await prefetch;
          await handleFetchCampaign();
        } catch (error) {
          console.error("Error loading campaign:", error);
          setLoadingData(false);
        }
      } else {
        setLoadingData(false);
        await prefetch;
      }
    };

    loadData();
  }, [id]);

  useEffect(() => {
    const state = location.state as { 
      newSegmentId?: number; 
      campaignFormData?: any; 
      rewardId?: number;
      rewardName?: string;
      clientCount?: number;
      estimatedImpact?: number;
      estimatedReach?: number;
      estimatedRoi?: number;
      promotionId?: number;
      promotionName?: string;
      clientSegmentIds?: number[];
    } | null;
    const newSegmentId = state?.newSegmentId;
    const savedFormData = state?.campaignFormData;
    const rewardId = state?.rewardId;
    const rewardName = state?.rewardName;
    const clientCount = state?.clientCount;
    const estimatedImpact = state?.estimatedImpact;
    const estimatedReach = state?.estimatedReach;
    const estimatedRoi = state?.estimatedRoi;
    const promotionId = state?.promotionId;
    const promotionName = state?.promotionName;
    const clientSegmentIds = state?.clientSegmentIds;
    
    if (newSegmentId || savedFormData || rewardId || promotionId || clientSegmentIds) {
      if (savedFormData) {
        form.setFieldsValue(savedFormData);
      }
      
      if (newSegmentId) {
        handleSearchClientSegments("").then(() => {
          const currentSegmentIds = form.getFieldValue("clientSegmentIds") || [];
          if (!currentSegmentIds.includes(newSegmentId)) {
            form.setFieldsValue({
              clientSegmentIds: [...currentSegmentIds, newSegmentId],
            });
          }
        });
      }
      
      if (clientSegmentIds && clientSegmentIds.length > 0 && !rewardId) {
        handleSearchClientSegments("").then(() => {
          form.setFieldsValue({
            clientSegmentIds: clientSegmentIds,
          });
        });
      }
      
      if (rewardId) {
        setFromRewardRef({ rewardId, rewardName, clientCount });
        handleSearchRewards("").then(() => {
          form.setFieldsValue({
            rewardId: rewardId,
            ...(rewardName ? { name: rewardName } : {}),
            ...(estimatedImpact != null ? { estimatedImpact } : {}),
            ...(estimatedReach != null ? { estimatedAudience: estimatedReach } : {}),
            ...(estimatedRoi != null ? { estimatedRoi } : {}),
          });
          // Fill any missing economics from the reward row / detail API.
          if (
            estimatedImpact == null ||
            estimatedReach == null ||
            estimatedRoi == null
          ) {
            applyEconomicsFromReward(rewardId);
          }
        });
      }
      
      if (promotionId) {
        handleSearchPromotions("").then(() => {
          form.setFieldsValue({
            promotionId: promotionId,
            ...(promotionName ? { name: promotionName } : {}),
          });
        });
      }
      
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const handleFetchCampaign = async () => {
    setLoadingData(true);
    try {
      const campaignData = await getCampaignById(id!);
      if (campaignData) {
        setCampaign(campaignData);
        form.setFieldsValue({
          name: campaignData.name,
          description: campaignData.description,
          campaignType: campaignData.campaignType,
          campaignStatus: campaignData.campaignStatus,
          estimatedImpact: campaignData.estimatedImpact,
          estimatedCost: campaignData.estimatedCost,
          estimatedRoi: campaignData.estimatedRoi,
          estimatedAudience: campaignData.estimatedAudience,
          validity:
            campaignData.validityStartDate && campaignData.validityEndDate
              ? [
                  moment(campaignData.validityStartDate),
                  moment(campaignData.validityEndDate),
                ]
              : null,
          promotionId: campaignData.promotion?.id,
          rewardId: campaignData.reward?.id,
        });
        setLoadingData(false);
        setLoadingSegments(true);
        try {
          const segments = await getCampaignClientSegmentIds(id!);
          if (segments?.length) {
            setClientSegments((prev) => {
              const byId = new Map(prev.map((s: any) => [s.id, s]));
              segments.forEach((s: { id: number; name: string }) =>
                byId.set(s.id, { id: s.id, name: s.name })
              );
              return Array.from(byId.values());
            });
            form.setFieldsValue({
              clientSegmentIds: segments.map((s) => s.id),
            });
          }
        } finally {
          setLoadingSegments(false);
        }
        return;
      }
    } catch (error) {
      message.error(t("Common-ErrorMessage"));
    } finally {
      setLoadingData(false);
    }
  };

  const handleSearchClientSegments = async (value: string) => {
    setLoadingSegments(true);
    try {
      const response = await getClientSegmentsFilteredLite(1, { name: value });
      setClientSegments((prev) => {
        const byId = new Map(prev.map((s: any) => [s.id, s]));
        (response?.clientSegments || []).forEach((s: any) => byId.set(s.id, s));
        return Array.from(byId.values());
      });
    } catch (error) {
      console.error("Error fetching client segments:", error);
      setClientSegments([]);
    } finally {
      setLoadingSegments(false);
    }
  };

  const handleSearchPromotions = async (value: string) => {
    try {
      const response = await getPromotions(1, { name: value });
      setPromotions(response?.promotions || []);
    } catch (error) {
      console.error("Error fetching promotions:", error);
      setPromotions([]);
    }
  };

  const handleSearchRewards = async (value: string) => {
    try {
      const response = await getRewards(1, { name: value });
      setRewards(response?.rewards || []);
    } catch (error) {
      console.error("Error fetching rewards:", error);
      setRewards([]);
    }
  };

  const applyEconomicsFromReward = async (rewardId: number) => {
    const fromList = rewards.find((r: any) => r.id === rewardId);
    if (
      fromList &&
      (fromList.estimatedImpact != null ||
        fromList.estimatedReach != null ||
        fromList.estimatedRoi != null)
    ) {
      form.setFieldsValue({
        ...(fromList.estimatedImpact != null
          ? { estimatedImpact: fromList.estimatedImpact }
          : {}),
        ...(fromList.estimatedReach != null
          ? { estimatedAudience: fromList.estimatedReach }
          : {}),
        ...(fromList.estimatedRoi != null
          ? { estimatedRoi: fromList.estimatedRoi }
          : {}),
      });
      return;
    }

    const reward = await getRewardById(String(rewardId));
    const data = reward?.data ?? reward;
    if (!data) return;
    form.setFieldsValue({
      ...(data.estimatedImpact != null
        ? { estimatedImpact: data.estimatedImpact }
        : {}),
      ...(data.estimatedReach != null
        ? { estimatedAudience: data.estimatedReach }
        : {}),
      ...(data.estimatedRoi != null ? { estimatedRoi: data.estimatedRoi } : {}),
    });
  };

  const handlePromotionChange = (value: any) => {
    if (value) {
      form.setFieldsValue({ rewardId: undefined });
    }
  };

  const handleRewardChange = (value: any) => {
    if (value) {
      form.setFieldsValue({ promotionId: undefined });
      applyEconomicsFromReward(Number(value));
    }
    if (value !== fromRewardRef?.rewardId) {
      setFromRewardRef(null);
    }
  };

  const handleOpenClientsModal = () => {
    setClientsModalOpen(true);
    setClientsModalPage(0);
  };

  useEffect(() => {
    if (!clientsModalOpen || !fromRewardRef) return;
    setClientsModalLoading(true);
    getRewardClients(String(fromRewardRef.rewardId), {
      page: clientsModalPage,
      size: clientsModalPageSize,
    })
      .then((data: any) => {
        setClientsModalData({
          content: data?.content ?? [],
          totalElements: data?.totalElements ?? 0,
        });
      })
      .catch(() => setClientsModalData({ content: [], totalElements: 0 }))
      .finally(() => setClientsModalLoading(false));
  }, [clientsModalOpen, fromRewardRef?.rewardId, clientsModalPage]);

  const handleCreateNewSegment = () => {
    const currentPath = location.pathname;
    const currentFormValues = form.getFieldsValue();
    navigate(`${COMMERCIAL_INTELLIGENCE_PATHS.segments}/create`, {
      state: { 
        returnToCampaign: currentPath,
        campaignFormData: currentFormValues,
      },
    });
  };

  const handleSave = async () => {
    await form.validateFields().then(async (values) => {
      setLoading(true);
      const {
        name,
        description,
        campaignType,
        campaignStatus,
        estimatedImpact,
        estimatedCost,
        estimatedRoi,
        estimatedAudience,
        validity,
        promotionId,
        rewardId,
        clientSegmentIds,
      } = values;

      if (promotionId && rewardId) {
        message.error(t("Common-CampaignPromotionOrRewardError"));
        setLoading(false);
        return;
      }

      const validityStartDate = validity
        ? validity[0]?.format("YYYY-MM-DD")
        : null;
      const validityEndDate = validity
        ? validity[1]?.format("YYYY-MM-DD")
        : null;

      let response;
      if (isEdit) {
        response = await updateCampaign(id!, {
          name,
          description,
          validityStartDate,
          validityEndDate,
          promotionId: promotionId || undefined,
          rewardId: rewardId || undefined,
          clientSegmentIds: clientSegmentIds || [],
          campaignType,
          campaignStatus,
          estimatedImpact,
          estimatedCost,
          estimatedRoi,
          estimatedAudience,
        });
      } else {
        response = await createCampaign({
          name,
          description,
          validityStartDate,
          validityEndDate,
          promotionId: promotionId || undefined,
          rewardId: rewardId || undefined,
          clientSegmentIds: fromRewardRef ? [] : (clientSegmentIds || []),
          campaignType,
          campaignStatus,
          estimatedImpact,
          estimatedCost,
          estimatedRoi,
          estimatedAudience,
        });
      }

      if (response) {
        const hasSegmentsProcessing =
          !isEdit && !fromRewardRef && clientSegmentIds && clientSegmentIds.length > 0;
        if (hasSegmentsProcessing) {
          message.info(t("Common-CampaignCreatingMessage"));
        } else {
          message.success(
            isEdit
              ? t("Common-UpdateSuccessMessage")
              : t("Common-CreateSuccessMessage")
          );
        }
        form.resetFields();
        navigate(getBasePath());
      } else message.error(t("Common-ErrorMessage"));

      setLoading(false);
    });
  };

  const getOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  if (loadingData) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <div>
            <Skeleton.Input style={{ width: 220, height: 20 }} active />
          </div>
          <div className={styles.actionButtonsWrapper}>
            <Skeleton.Button style={{ width: 90, height: 32 }} active />
          </div>
        </div>
        <div className={styles.contentWrapper}>
          <div className={styles.campaignInfoWrapper}>
            <Skeleton active paragraph={{ rows: 12 }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <NotificationOutlined />,
                label: t("NavBar-Campaigns"),
                path: getBasePath(),
              },
              {
                label: isEdit
                  ? t("Common-EditCampaign")
                  : t("Common-NewCampaign"),
                isCurrentPage: true,
              },
            ]}
          />
        </div>
        <div className={styles.actionButtonsWrapper}>
          <Button
            onClick={handleSave}
            className={styles.saveButton}
            type="primary"
            loading={loading}
          >
            {isEdit ? t("Common-Update") : t("Common-Add")}
          </Button>
        </div>
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.campaignInfoWrapper}>
          <Form
            name="newCampaign"
            layout="vertical"
            initialValues={{ remember: true }}
            form={form}
            autoComplete="off"
            className={styles.newForm}
          >
            <div className={styles.sectionTitle}>
              {t("Common-BasicInformation")}
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Name")}
                name="name"
                rules={[
                  {
                    required: true,
                    message: t("Common-NameNotEntered"),
                  },
                ]}
              >
                <Input placeholder={t("Common-Name")} />
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Description")}
                name="description"
              >
                <Input.TextArea
                  placeholder={t("Common-Description")}
                  rows={3}
                />
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Validity")}
                name="validity"
              >
                <RangePicker
                  className={styles.dateSelector}
                  placeholder={[
                    t("Common-ValidFrom"),
                    t("Common-ValidTo"),
                  ]}
                />
              </Form.Item>
            </div>
            <div className={styles.sectionTitle}>
              {t("Common-Metrics")}
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Type")}
                name="campaignType"
              >
                <Select allowClear placeholder={t("Common-Type")}>
                  <Select.Option value="INCENTIVE">
                    {t("Common-CampaignTypeIncentive")}
                  </Select.Option>
                  <Select.Option value="PROMOTION">
                    {t("Common-CampaignTypePromotion")}
                  </Select.Option>
                  <Select.Option value="SEGMENT">
                    {t("Common-CampaignTypeSegment")}
                  </Select.Option>
                  <Select.Option value="ACTIVATION">
                    {t("Common-CampaignTypeActivation")}
                  </Select.Option>
                </Select>
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Status")}
                name="campaignStatus"
              >
                <Select allowClear placeholder={t("Common-Status")}>
                  <Select.Option value="FUTURA">
                    {t("Common-CampaignStatusFuture")}
                  </Select.Option>
                  <Select.Option value="ACTIVA">
                    {t("Common-CampaignStatusActive")}
                  </Select.Option>
                  <Select.Option value="FINALIZADA">
                    {t("Common-CampaignStatusFinished")}
                  </Select.Option>
                </Select>
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-EstimatedImpact")}
                name="estimatedImpact"
              >
                <InputNumber
                  className={styles.inputNumber}
                  min={0}
                  step={0.01}
                  placeholder={t("Common-EstimatedImpact")}
                />
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-EstimatedCost")}
                name="estimatedCost"
              >
                <InputNumber
                  className={styles.inputNumber}
                  min={0}
                  step={0.01}
                  placeholder={t("Common-EstimatedCost")}
                />
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-EstimatedROI")}
                name="estimatedRoi"
              >
                <InputNumber
                  className={styles.inputNumber}
                  step={0.01}
                  placeholder={t("Common-EstimatedROI")}
                />
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-EstimatedAudience")}
                name="estimatedAudience"
              >
                <InputNumber
                  className={styles.inputNumber}
                  min={0}
                  step={1}
                  placeholder={t("Common-EstimatedAudience")}
                />
              </Form.Item>
            </div>
            <div className={styles.sectionTitle}>
              {t("Common-Associations")}
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Promotion")}
                name="promotionId"
              >
                <Select
                  allowClear
                  showSearch
                  filterOption={false}
                  onSearch={handleSearchPromotions}
                  onChange={handlePromotionChange}
                  placeholder={t("Common-SearchByName")}
                >
                  {getOptions(promotions)}
                </Select>
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Reward")}
                name="rewardId"
              >
                <Select
                  allowClear
                  showSearch
                  filterOption={false}
                  onSearch={handleSearchRewards}
                  onChange={handleRewardChange}
                  placeholder={t("Common-SearchByName")}
                >
                  {getOptions(rewards)}
                </Select>
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              {fromRewardRef ? (
                <Form.Item
                  className={styles.formItem}
                  label={t("Common-ClientSegments")}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span>
                      {t("Common-ClientsFromIncentive", {
                        name: fromRewardRef.rewardName ?? "",
                        count: fromRewardRef.clientCount ?? 0,
                      })}
                    </span>
                    <Button
                      type="link"
                      size="small"
                      icon={<UserOutlined />}
                      onClick={handleOpenClientsModal}
                      style={{ padding: 0, height: "auto" }}
                    >
                      {t("Common-ViewClients")}
                    </Button>
                  </div>
                </Form.Item>
              ) : (
                <Form.Item
                  className={styles.formItem}
                  label={
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>{t("Common-ClientSegments")}</span>
                      <Button
                        type="link"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={handleCreateNewSegment}
                        style={{ padding: 0, height: "auto" }}
                      >
                        {t("Common-NewClientSegment")}
                      </Button>
                    </div>
                  }
                  name="clientSegmentIds"
                >
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    loading={loadingSegments}
                    filterOption={(input: string, option: any) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onSearch={handleSearchClientSegments}
                    placeholder={t("Common-SearchByName")}
                  >
                    {getOptions(clientSegments)}
                  </Select>
                </Form.Item>
              )}
            </div>
            <Modal
              title={t("Common-ViewClients")}
              visible={clientsModalOpen}
              onCancel={() => setClientsModalOpen(false)}
              footer={null}
              width={640}
            >
              {clientsModalLoading ? (
                <div style={{ textAlign: "center", padding: 24 }}>
                  <Spin />
                </div>
              ) : (
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={clientsModalData.content}
                  columns={[
                    { title: "ID", dataIndex: "id", width: 80 },
                    { title: t("Common-Name"), dataIndex: "name" },
                  ]}
                  pagination={{
                    current: clientsModalPage + 1,
                    pageSize: clientsModalPageSize,
                    total: clientsModalData.totalElements,
                    showSizeChanger: false,
                    onChange: (page) => setClientsModalPage(page - 1),
                  }}
                />
              )}
            </Modal>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default NewCampaign;

